
export type GraphQLRequest = {
  operationName: string;
  query?: string;
  variables?: Record<string, unknown>;
  extensions?: {
    persistedQuery?: { version: number; sha256Hash: string };
  };
};

export type GraphQLResponse<T> = {
  data?: T;
  errors?: Array<{ message: string; extensions?: Record<string, unknown> }>;
};

export type ClientOptions = {
  url: string;
  getAuthToken: () => string | undefined;
  getTenant: () => string | undefined;
  apqOnly?: boolean;
};

export class GraphQLClient {
  private url: string;
  private getAuthToken: ClientOptions['getAuthToken'];
  private getTenant: ClientOptions['getTenant'];
  private apqOnly: boolean;

  constructor(opts: ClientOptions) {
    this.url = opts.url;
    this.getAuthToken = opts.getAuthToken;
    this.getTenant = opts.getTenant;
    this.apqOnly = !!opts.apqOnly;
  }

  async request<T>(req: GraphQLRequest): Promise<GraphQLResponse<T>> {
    if (!req.operationName) {
      throw new Error('operationName is required');
    }
    if (this.apqOnly) {
      const hasPersisted = Boolean(req.extensions?.persistedQuery?.sha256Hash);
      if (!hasPersisted) {
        throw new Error(`APQ-only mode: missing persistedQuery sha256Hash for ${req.operationName}`);
      }
    }
    performance.mark(`gql:${req.operationName}:start`);
    const headers: Record<string, string> = {
      'content-type': 'application/json',
    };
    // Correlation header: prefer existing, else create
    try {
      const existing = (globalThis as any).__TRACE_ID__ as string | undefined;
      const traceId =
        existing ||
        `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}` ||
        `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      (globalThis as any).__TRACE_ID__ = traceId;
      headers['x-correlation-id'] = traceId;
    } catch { }
    const token = this.getAuthToken();
    if (token) headers['authorization'] = `Bearer ${token}`;
    const tenant = this.getTenant();
    if (tenant) headers['x-tenant'] = tenant;

    // First attempt: APQ hash-only request if provided
    const body = JSON.stringify(req);
    let res = await fetch(this.url, { method: 'POST', headers, body });

    // Handle non-JSON responses (e.g., auth errors)
    let json: GraphQLResponse<T> & { errors?: any[] };
    const responseText = await res.text();
    try {
      json = JSON.parse(responseText) as GraphQLResponse<T> & { errors?: any[] };
    } catch (parseError) {
      // Backend returned non-JSON (likely plain text error)
      json = {
        errors: [{
          message: responseText || `HTTP ${res.status}: ${res.statusText}`,
          extensions: {
            code: res.status === 401 ? 'UNAUTHENTICATED' : 'NETWORK_ERROR',
            httpStatus: res.status
          }
        }]
      };
    }

    const notFound = json?.errors?.some((e) => e?.extensions?.code === 'PERSISTED_QUERY_NOT_FOUND');
    const unauth = json?.errors?.some((e) => e?.extensions?.code === 'UNAUTHENTICATED');
    const forbidden = json?.errors?.some((e) => e?.extensions?.code === 'FORBIDDEN');
    if (unauth) {
      try { window.dispatchEvent(new Event('gql:unauthenticated')); } catch { }
    }
    if (forbidden) {
      try {
        const err = (json.errors || []).find((e: any) => e?.extensions?.code === 'FORBIDDEN');
        const detail = { requiredScopes: err?.extensions?.requiredScopes };
        window.dispatchEvent(new CustomEvent('gql:forbidden', { detail }));
      } catch { }
    }
    const hasPersisted = Boolean(req.extensions?.persistedQuery?.sha256Hash);
    const hasQuery = Boolean(req.query);
    if (notFound && hasPersisted && hasQuery && !this.apqOnly) {
      // Warm path: resend with full query
      const warmReq: GraphQLRequest = { ...req };
      res = await fetch(this.url, {
        method: 'POST',
        headers,
        body: JSON.stringify(warmReq),
      });

      // Handle non-JSON responses for warm request too
      const warmResponseText = await res.text();
      try {
        json = JSON.parse(warmResponseText) as GraphQLResponse<T>;
      } catch (parseError) {
        json = {
          errors: [{
            message: warmResponseText || `HTTP ${res.status}: ${res.statusText}`,
            extensions: {
              code: res.status === 401 ? 'UNAUTHENTICATED' : 'NETWORK_ERROR',
              httpStatus: res.status
            }
          }]
        };
      }
    }
    performance.mark(`gql:${req.operationName}:end`);
    performance.measure(`gql:${req.operationName}`, `gql:${req.operationName}:start`, `gql:${req.operationName}:end`);
    return json;
  }
}

// Global singleton configuration so host can configure and remotes can use
let globalClient: GraphQLClient | undefined;

export function configureClient(opts: ClientOptions) {
  globalClient = new GraphQLClient(opts);
  try { (globalThis as any).__GQL_URL__ = opts.url; } catch { }
}

export function getClient(): GraphQLClient {
  if (!globalClient) {
    // Fallback: construct client from runtime globals so remotes work even without explicit configureClient call
    const url = (globalThis as any).__GQL_URL__ || (typeof window !== 'undefined' ? `${window.location.origin}/graphql` : '/graphql');
    const getAuthToken = () => {
      try { return (globalThis as any).__AUTH_TOKEN__ || sessionStorage.getItem('auth:token') || undefined; } catch { return undefined; }
    };
    const getTenant = () => {
      try { return (globalThis as any).__TENANT__ || sessionStorage.getItem('tenant') || ''; } catch { return ''; }
    };
    globalClient = new GraphQLClient({ url: String(url), getAuthToken, getTenant, apqOnly: false });
  }
  return globalClient as GraphQLClient;
}

export async function sha256(input: string): Promise<string> {
  try {
    const subtle: SubtleCrypto | undefined = (globalThis as any)?.crypto?.subtle;
    if (!subtle) return '';
    const enc = new TextEncoder();
    const data = enc.encode(input);
    const hash = await subtle.digest('SHA-256', data);
    const bytes = Array.from(new Uint8Array(hash));
    return bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return '';
  }
}



